import { memo, useMemo } from 'react';
import { TextField, Box } from '@mui/material';
import { cohortsApi, type CohortForAdminListsAttrs } from 'Cohorts';
import { type ProgramType } from 'Program/Program.types';
import {
    useListGradersQuery,
    useListProjectRequirementIdentifiersQuery,
    useListProjectVariantLabelsQuery,
    type SubmissionsFilters,
    type VisibleFilterType,
} from '../api';
import {
    ProgramTypeFilterSelect,
    getProgramTypeLabel,
    GraderMultiSelect,
    GradingStatusMultiSelect,
    getGradingStatusLabel,
    SaoDisplayStatusMultiSelect,
    getSaoDisplayStatusLabel,
    ScoreMultiSelect,
    ActiveFilterChips,
    type FilterChip,
    CohortMultiSelect,
    RequirementMultiSelect,
    ProjectVariantMultiSelect,
    NO_VARIANT_SENTINEL,
    TEXT_FIELD_SX,
    toArray,
    useSubmissionsFilterState,
} from '../shared';

interface SubmissionsFiltersBarProps {
    filters: SubmissionsFilters;
    onFiltersChange: (filters: SubmissionsFilters) => void;
    /** Which filters to show. If undefined, all filters are shown. */
    visibleFilters?: readonly VisibleFilterType[];
    /** Keep dropdowns inside the DOM tree (required when used inside a modal with focus traps) */
    disablePortal?: boolean;
}

// oxlint-disable-next-line max-lines-per-function
function SubmissionsFiltersBarComponent({
    filters,
    onFiltersChange,
    visibleFilters,
    disablePortal,
}: SubmissionsFiltersBarProps) {
    const isFilterVisible = (filterType: VisibleFilterType) => !visibleFilters || visibleFilters.includes(filterType);
    const { data: gradersData } = useListGradersQuery();
    const { data: requirementIdentifiersData } = useListProjectRequirementIdentifiersQuery();
    const { data: variantLabelsData } = useListProjectVariantLabelsQuery({ context: 'admin' });
    const { data: cohortsData } = cohortsApi.useGetCohortsForAdminQuery();

    const graders = useMemo(() => gradersData?.graders ?? [], [gradersData?.graders]);
    const variantLabels = useMemo(() => variantLabelsData?.variantLabels ?? [], [variantLabelsData?.variantLabels]);
    const allCohorts = useMemo<CohortForAdminListsAttrs[]>(() => cohortsData ?? [], [cohortsData]);

    // Sort requirement identifiers alphabetically
    const sortedRequirementIdentifiers = useMemo(() => {
        const identifiers = requirementIdentifiersData?.projectRequirementIdentifiers ?? [];
        return [...identifiers]
            .sort((a, b) => a.requirementIdentifier.localeCompare(b.requirementIdentifier))
            .map(r => r.requirementIdentifier);
    }, [requirementIdentifiersData?.projectRequirementIdentifiers]);

    // Filter cohorts by selected program types
    const filteredCohorts = useMemo(() => {
        const programTypes = toArray(filters.programType);
        if (programTypes.length === 0) return allCohorts;
        return allCohorts.filter(c => programTypes.includes(c.programType));
    }, [allCohorts, filters.programType]);

    // Use shared filter state management
    const { handleFilterChange, handleChipDelete, handleClearAll } = useSubmissionsFilterState({
        filters,
        onFiltersChange,
        allCohorts,
    });

    // Build active filter chips
    const filterChips = useMemo<FilterChip[]>(() => {
        const chips: FilterChip[] = [];

        // SAO Display Status chips (one per selected status)
        toArray(filters.saoDisplayStatus).forEach(status => {
            chips.push({
                key: `saoDisplayStatus-${status}`,
                label: `S&O Check: ${getSaoDisplayStatusLabel(status)}`,
            });
        });

        // Grading Status chips (one per selected status)
        toArray(filters.gradingStatus).forEach(status => {
            chips.push({
                key: `gradingStatus-${status}`,
                label: `Status: ${getGradingStatusLabel(status)}`,
            });
        });

        // Program Type chips (one per selected program)
        toArray(filters.programType).forEach(programType => {
            chips.push({
                key: `programType-${programType}`,
                label: `Program: ${getProgramTypeLabel(programType as ProgramType)}`,
            });
        });

        // Cohort chips (one per selected cohort)
        toArray(filters.cohortId).forEach(cohortId => {
            const cohort = allCohorts.find(c => c.id === cohortId);
            chips.push({
                key: `cohortId-${cohortId}`,
                label: `Cohort: ${cohort?.name ?? cohortId}`,
            });
        });

        // Requirement chips (one per selected requirement)
        toArray(filters.requirementIdentifier).forEach(identifier => {
            chips.push({
                key: `requirementIdentifier-${identifier}`,
                label: `Requirement: ${identifier}`,
            });
        });

        // Grader chips (one per selected grader)
        toArray(filters.graderId).forEach(graderId => {
            const grader = graders.find(g => g.id === graderId);
            chips.push({
                key: `graderId-${graderId}`,
                label: `Grader: ${grader?.name ?? graderId}`,
            });
        });

        // Project Variant chips (one per selected variant)
        toArray(filters.projectVariant).forEach(variant => {
            const label = variant === NO_VARIANT_SENTINEL ? 'No Variant Selected' : variant;
            chips.push({
                key: `projectVariant-${variant}`,
                label: `Variant: ${label}`,
            });
        });

        // Score chips (one per selected score value)
        toArray(filters.score).forEach(score => {
            chips.push({
                key: `score-${score}`,
                label: `Score: ${score}`,
            });
        });

        if (filters.studentEmail) {
            chips.push({ key: 'studentEmail', label: `Student: ${filters.studentEmail}` });
        }

        if (filters.olderThanDays) {
            chips.push({ key: 'olderThanDays', label: `Older than: ${filters.olderThanDays} days` });
        }

        return chips;
    }, [filters, graders, allCohorts]);

    return (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'flex-start' }}>
                {/* SAO Display Status - Multi-select (S&O Review tab only) */}
                {isFilterVisible('saoDisplayStatus') && (
                    <SaoDisplayStatusMultiSelect
                        value={filters.saoDisplayStatus}
                        onChange={statuses => handleFilterChange('saoDisplayStatus', statuses)}
                        disablePortal={disablePortal}
                    />
                )}

                {/* Grading Status - Multi-select */}
                {isFilterVisible('gradingStatus') && (
                    <GradingStatusMultiSelect
                        value={filters.gradingStatus}
                        onChange={statuses => handleFilterChange('gradingStatus', statuses)}
                        disablePortal={disablePortal}
                    />
                )}

                {/* Program Type - using shared component in multi mode */}
                {isFilterVisible('programType') && (
                    <ProgramTypeFilterSelect
                        value={toArray(filters.programType)}
                        onChange={value => handleFilterChange('programType', value.length > 0 ? value : undefined)}
                        multiple
                        label="Program"
                        size="small"
                    />
                )}

                {/* Cohort - Multi-select filtered by program type */}
                {isFilterVisible('cohortId') && (
                    <CohortMultiSelect
                        cohorts={filteredCohorts}
                        value={filters.cohortId}
                        onChange={cohortIds => handleFilterChange('cohortId', cohortIds)}
                        disablePortal={disablePortal}
                    />
                )}

                {/* Requirement Identifier - Multi-select */}
                {isFilterVisible('requirementIdentifier') && (
                    <RequirementMultiSelect
                        options={sortedRequirementIdentifiers}
                        value={filters.requirementIdentifier}
                        onChange={identifiers => handleFilterChange('requirementIdentifier', identifiers)}
                    />
                )}

                {/* Grader - Multi-select using shared component */}
                {isFilterVisible('graderId') && (
                    <GraderMultiSelect
                        graders={graders}
                        value={filters.graderId}
                        onChange={graderIds => handleFilterChange('graderId', graderIds)}
                        label="Grader"
                    />
                )}

                {/* Student - search by name, email, or ID */}
                {isFilterVisible('studentEmail') && (
                    <TextField
                        size="small"
                        label="Student"
                        placeholder="Name, email, or ID"
                        value={filters.studentEmail ?? ''}
                        onChange={e => handleFilterChange('studentEmail', e.target.value || undefined)}
                        sx={{ minWidth: 200, ...TEXT_FIELD_SX }}
                    />
                )}

                {/* Score - Multi-select (0-5) */}
                {isFilterVisible('score') && (
                    <ScoreMultiSelect
                        value={filters.score}
                        onChange={scores => handleFilterChange('score', scores)}
                        disablePortal={disablePortal}
                    />
                )}

                {/* Older Than Days - Keep as single number input */}
                {isFilterVisible('olderThanDays') && (
                    <TextField
                        size="small"
                        type="number"
                        label="Older Than (days)"
                        value={filters.olderThanDays ?? ''}
                        onChange={e =>
                            handleFilterChange(
                                'olderThanDays',
                                e.target.value ? parseInt(e.target.value, 10) : undefined,
                            )
                        }
                        sx={{ width: 150, ...TEXT_FIELD_SX }}
                    />
                )}

                {/* Project Variant - Multi-select */}
                {isFilterVisible('projectVariant') && (
                    <ProjectVariantMultiSelect
                        variantLabels={variantLabels}
                        value={filters.projectVariant}
                        onChange={variants => handleFilterChange('projectVariant', variants)}
                        disablePortal={disablePortal}
                    />
                )}
            </Box>

            {/* Active Filter Chips */}
            <ActiveFilterChips
                chips={filterChips}
                onDelete={handleChipDelete}
                onClearAll={handleClearAll}
                showClearAll
            />
        </Box>
    );
}

export const SubmissionsFiltersBar = memo(SubmissionsFiltersBarComponent);
